// src/contexts/ToastContext.jsx
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const ToastContext = createContext(null);

function uid() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const removeToast = useCallback((id) => {
    const t = timersRef.current.get(id);
    if (t) {
      clearTimeout(t);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const pushToast = useCallback((opts) => {
    const id = uid();
    const toast = {
      id,
      type: opts?.type || "info", // info | success | error
      message: String(opts?.message || ""),
      durationMs: Number(opts?.durationMs ?? 6500),
      onClick: typeof opts?.onClick === "function" ? opts.onClick : null,
    };

    setToasts((prev) => [toast, ...prev].slice(0, 5));

    if (toast.durationMs > 0) {
      const timer = setTimeout(() => removeToast(id), toast.durationMs);
      timersRef.current.set(id, timer);
    }

    return id;
  }, [removeToast]);

  // ✅ Callbacks estáveis (evita loop de re-render em listeners que dependem de info/error)
  const info = useCallback((message, opts = {}) => pushToast({ ...opts, type: "info", message }), [pushToast]);
  const success = useCallback((message, opts = {}) => pushToast({ ...opts, type: "success", message }), [pushToast]);
  const error = useCallback((message, opts = {}) => pushToast({ ...opts, type: "error", message }), [pushToast]);

  const api = useMemo(() => {
    return {
      toasts,
      pushToast,
      removeToast,
      info,
      success,
      error,
    };
  }, [toasts, pushToast, removeToast, info, success, error]);

  return <ToastContext.Provider value={api}>{children}</ToastContext.Provider>;
}

export function useToast() {
  const ctx = useContext(ToastContext);
  // ⚠️ Segurança: em alguns cenários (hot reload, entrypoints diferentes, erro de montagem)
  // o provider pode não estar disponível. Não derruba o app — retorna API no-op.
  if (!ctx) {
    const noop = () => {};
    return {
      toasts: [],
      pushToast: () => null,
      removeToast: noop,
      info: noop,
      success: noop,
      error: noop,
    };
  }
  return ctx;
}
